import {
  buildExpenseData,
  costTypeLabel,
  formatAmount,
  groupAmountSummary,
  localizeLabel,
  payTypeLabel,
  PERIOD_COST_TYPE,
  periodTypeLabel,
  totalByCurrency,
  type ExpensePreApprovalAttributes,
  type ExpensePreApprovalData,
  type ExpensePreApprovalState,
} from '@/utils/approval/expensePreApproval';

/**
 * 205 attributes.content — 조회 화면 HTML 스냅샷.
 * 상신(저장) 시점의 값·라벨·금액을 HTML 로 고정해, 결재 후 코드·계산이 바뀌어도 결재자가 본 내용 그대로 보임.
 * 스타일은 넣지 않음(클래스만) — 조회 화면 CSS 가 입힘
 */

/** 이 표시가 있어야 V2 스냅샷 — 레거시 CL01 은 폼 DOM 이라 data 로 다시 만듦 */
export const EXPENSE_SNAPSHOT_ATTR = 'data-expense-snapshot';
const SNAPSHOT_VERSION = '1';

type Translate = (key: string) => string;

const esc = (value: string | number) =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const FLAG_EXT: Readonly<Record<string, string>> = {
  HKD: 'png',
  TWD: 'png',
  MOP: 'png',
};

const currencyHtml = (currency: string) =>
  `<span class="currency-label"><img class="currency-flag" src="/icons/flags/${esc(
    currency.toLowerCase(),
  )}.${FLAG_EXT[currency] ?? 'svg'}" alt="" />${esc(currency)}</span>`;

const field = (label: string, value: string, filled = false) =>
  `<div class="expense-field"><span class="expense-field-label">${esc(
    label,
  )}</span><div class="approval-value-box${filled ? ' is-filled' : ''}">${esc(
    value,
  )}</div></div>`;

const summaryTable = (data: ExpensePreApprovalData, t: Translate) => {
  const rows = data.amountSummary ?? [];
  const groups = groupAmountSummary(rows);
  const totals = totalByCurrency(rows);
  const body = groups.length
    ? groups
        .map((g) =>
          g.rows
            .map(
              (r, i) =>
                `<tr>${
                  i === 0
                    ? `<td rowspan="${g.rows.length}">${esc(g.vendor)}</td>`
                    : ''
                }<td>${esc(
                  r.summary,
                )}</td><td class="align-right">${formatAmount(
                  r.amount,
                  r.currency,
                )}</td><td class="align-center">${currencyHtml(
                  r.currency,
                )}</td></tr>`,
            )
            .join(''),
        )
        .join('')
    : `<tr><td colspan="4" class="align-center">${esc(
        t('데이터가 없습니다.'),
      )}</td></tr>`;
  const foot = (totals.length ? totals : [{ currency: 'KRW', amount: 0 }])
    .map(
      (x, i, arr) =>
        `<tr>${
          i === 0
            ? `<td rowspan="${arr.length}"><strong>${esc(
                t('총 지급액'),
              )}</strong></td><td rowspan="${arr.length}"></td>`
            : ''
        }<td class="align-right"><strong>${formatAmount(
          x.amount,
          x.currency,
        )}</strong></td><td class="align-center">${currencyHtml(
          x.currency,
        )}</td></tr>`,
    )
    .join('');
  return `<table class="approval-form-table expense-amount-summary-table"><colgroup><col /><col /><col width="250" /><col width="96" /></colgroup><thead><tr><th>${esc(
    t('업체명'),
  )}</th><th>${esc(t('헤더적요'))}</th><th>${esc(
    t('총액 (VAT별도)'),
  )}</th><th>${esc(
    t('통화'),
  )}</th></tr></thead><tbody>${body}</tbody><tfoot>${foot}</tfoot></table>`;
};

const vendorCard = (
  item: ExpensePreApprovalData['items'][number],
  showPeriod: boolean,
  t: Translate,
) => {
  const payLabel = payTypeLabel(item.payType.value)
    ? t(payTypeLabel(item.payType.value))
    : item.payType.label;
  const periodLabel = periodTypeLabel(item.periodType.value)
    ? t(periodTypeLabel(item.periodType.value))
    : item.periodType.label || item.periodType.value;
  const rows = item.details
    .map(
      (d) =>
        `<tr><td>${esc(d.itemName)}</td><td class="align-center">${esc(
          d.quantity,
        )}</td><td class="align-right">${formatAmount(
          d.unitPrice,
          d.currency,
        )}</td><td class="align-right">${formatAmount(
          d.netAmount,
          d.currency,
        )}</td><td class="align-center">${currencyHtml(
          d.currency,
        )}</td><td>${esc(d.remark)}</td></tr>`,
    )
    .join('');
  return `<div class="expense-vendor-card">${field(
    t('업체명'),
    item.vendor.value,
  )}${field(t('헤더적요'), item.summary.value)}${field(
    t('지급방식'),
    payLabel,
  )}${
    showPeriod && item.periodType.value ? field(t('단위'), periodLabel) : ''
  }<table class="approval-form-table expense-detail-table"><colgroup><col /><col width="72" /><col width="120" /><col width="120" /><col width="96" /><col /></colgroup><thead><tr><th>${esc(
    t('품목'),
  )}</th><th>${esc(t('수량'))}</th><th>${esc(t('단가'))}</th><th>${esc(
    t('공급가액'),
  )}</th><th>${esc(t('통화'))}</th><th>${esc(
    t('비고'),
  )}</th></tr></thead><tbody>${rows}</tbody></table></div>`;
};

/** 조회 화면 HTML — 비용구분, 지급 총액, 지급 정보(여닫기, 기본 접힘). 레거시 조회 폼 구조 */
export const buildExpenseSnapshotHtml = (
  data: ExpensePreApprovalData,
  t: Translate,
): string => {
  const costType = data.classification_opt.value;
  const costLabel = costTypeLabel(costType)
    ? t(costTypeLabel(costType))
    : data.classification_opt.label;
  const showPeriod = costType === PERIOD_COST_TYPE;
  return `<div class="expense-pre-approval-snapshot" ${EXPENSE_SNAPSHOT_ATTR}="${SNAPSHOT_VERSION}"><div class="expense-section"><strong class="expense-label">${esc(
    t('비용구분'),
  )}</strong><div class="approval-value-box is-filled">${esc(
    costLabel,
  )}</div></div><div class="expense-section-lg"><strong class="expense-label">${esc(
    t('지급 총액'),
  )}</strong>${summaryTable(
    data,
    t,
  )}</div><details class="expense-payment-info"><summary class="approval-section-toggle"><strong>${esc(
    t('지급 정보'),
  )}</strong><span class="toggle-icon" aria-hidden="true"></span></summary>${data.items
    .map((item) => vendorCard(item, showPeriod, t))
    .join('')}</details></div>`;
};

/** 저장 attributes — data(편집 복원용) + content(작성자 언어 기준 조회 HTML 스냅샷) */
export const buildExpenseAttributes = (
  state: ExpensePreApprovalState | null | undefined,
  subject: string,
): ExpensePreApprovalAttributes | null => {
  if (!state) return null;
  const data = buildExpenseData(state, subject);
  return {
    content: buildExpenseSnapshotHtml(data, localizeLabel),
    data,
  };
};

export const isExpenseSnapshotHtml = (content: unknown): content is string =>
  typeof content === 'string' && content.includes(`${EXPENSE_SNAPSHOT_ATTR}="`);

// ===== 조회 시 허용 목록 필터 =====
// attributes.content 는 API 로 누구나 바꿔 넣을 수 있어, 스냅샷이 쓰는 태그·속성만 남김

const ALLOWED_TAGS = new Set([
  'div',
  'span',
  'strong',
  'table',
  'colgroup',
  'col',
  'thead',
  'tbody',
  'tfoot',
  'tr',
  'th',
  'td',
  'details',
  'summary',
  'img',
]);
const ALLOWED_ATTRS: Readonly<Record<string, ReadonlySet<string>>> = {
  '*': new Set(['class', 'aria-hidden']),
  div: new Set(['class', EXPENSE_SNAPSHOT_ATTR]),
  td: new Set(['class', 'colspan', 'rowspan']),
  th: new Set(['class', 'colspan', 'rowspan']),
  col: new Set(['width']),
  img: new Set(['class', 'src', 'alt']),
};
const FLAG_SRC = /^\/icons\/flags\/[a-z]{3}\.(svg|png)$/;
const NUMERIC = /^\d{1,4}$/;

const cleanElement = (el: Element) => {
  Array.from(el.children).forEach((child) => {
    if (!ALLOWED_TAGS.has(child.tagName.toLowerCase())) {
      child.remove();
      return;
    }
    cleanElement(child);
  });
  const tag = el.tagName.toLowerCase();
  const allowed = Object.hasOwn(ALLOWED_ATTRS, tag)
    ? ALLOWED_ATTRS[tag]
    : ALLOWED_ATTRS['*'];
  Array.from(el.attributes).forEach(({ name, value }) => {
    const ok =
      allowed.has(name) &&
      !(tag === 'img' && name === 'src' && !FLAG_SRC.test(value)) &&
      !(['colspan', 'rowspan', 'width'].includes(name) && !NUMERIC.test(value));
    if (!ok) el.removeAttribute(name);
  });
};

/** 스냅샷 HTML 을 허용 태그·속성만 남겨 돌려줌. 브라우저 밖(SSR)이면 빈 문자열 */
export const sanitizeExpenseSnapshotHtml = (html: string): string => {
  if (typeof DOMParser === 'undefined') return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const root = doc.body.firstElementChild;
  if (
    root?.tagName.toLowerCase() !== 'div' ||
    !root.hasAttribute(EXPENSE_SNAPSHOT_ATTR)
  ) {
    return '';
  }
  Array.from(doc.body.children).forEach((el) => {
    if (el !== root) el.remove();
  });
  cleanElement(root);
  return root.outerHTML;
};
